import { useMemo, useRef } from "react";
import { EMPTY_EDGES, EMPTY_NODES } from "../../core";
import { buildEmptyStepPlaceholder, resolveEmptyStepAnchor } from "../../core/emptyStepPlaceholder";
const EMPTY_GRAPH = {
    nodes: EMPTY_NODES,
    edges: EMPTY_EDGES
};
/**
 * The editor's empty-step placeholder layer: the placeholder node and its edge
 * from the start event while the draft is empty, empty arrays otherwise. Once
 * the placeholder turned into a step and that step's detail panel opened, the
 * step is revealed beside the panel.
 */ export function useEmptyStepPlaceholderLayer({ process, openAssignments, nodes, onAddFirstStep, revealNextSelection }) {
    const placeholderShownRef = useRef(false);
    const activate = useMemo(()=>onAddFirstStep && (async (startEventId, position)=>{
            const cancelReveal = revealNextSelection();
            await onAddFirstStep(startEventId, position);
            // The placeholder is back once the create settled: it failed.
            if (placeholderShownRef.current) cancelReveal();
        }), [
        onAddFirstStep,
        revealNextSelection
    ]);
    const anchor = useMemo(()=>activate ? resolveEmptyStepAnchor({
            process,
            openAssignments,
            nodes
        }) : null, [
        activate,
        process,
        openAssignments,
        nodes
    ]);
    // Keyed on primitives: `nodes` churns on every measurement, and a fresh
    // placeholder ref each time would send xyflow into a re-measure loop.
    const startEventId = anchor?.startEventId;
    const x = anchor?.x;
    const y = anchor?.y;
    const graph = useMemo(()=>{
        if (!activate || startEventId === undefined || x === undefined || y === undefined) {
            return EMPTY_GRAPH;
        }
        const { node, edge } = buildEmptyStepPlaceholder({
            startEventId,
            x,
            y
        }, activate);
        return {
            nodes: [
                node
            ],
            edges: [
                edge
            ]
        };
    }, [
        activate,
        startEventId,
        x,
        y
    ]);
    placeholderShownRef.current = graph.nodes.length > 0;
    return graph;
}

//# sourceMappingURL=useEmptyStepPlaceholderLayer.js.map